import React, { useMemo, useState } from 'react';
import { Box, Button, Typography, makeStyles } from '@material-ui/core';
import Modal from '../Modals/Modal';

const useStyles = makeStyles(() => ({
    wrapper: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 4,
    },
    chip: {
        fontSize: 12,
        maxWidth: 160,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        borderRadius: 8,
        padding: '2px 10px',
        backgroundColor: '#F4EBFC',
        border: '1px solid #43419E',
        color: '#43419E',
    },
    moreChip: {
        cursor: 'pointer',
        minWidth: 'auto',
        textTransform: 'none',
        fontSize: 12,
        fontWeight: 700,
        borderRadius: 8,
        padding: '2px 10px',
        backgroundColor: '#43419E',
        border: '1px solid #43419E',
        color: '#fff',
        '&:hover': {
            backgroundColor: '#37357f',
        },
    },
    modalList: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
        maxHeight: '50vh',
        overflowY: 'auto',
    },
    modalText: {
        fontSize: 14,
        color: '#14112D',
        wordBreak: 'break-word',
        whiteSpace: 'pre-wrap',
    },
    empty: {
        fontSize: 16,
        fontWeight: 700,
        color: '#14112D',
    },
}));

interface TruncatedListProps {
    /** List mode: renders up to `limit` entries as pills. */
    items?: (string | number)[];
    /** Long-string mode: clipped to `charLimit` characters. */
    text?: string;
    /** Inline entries shown before collapsing (list mode). */
    limit?: number;
    /** Inline characters shown before collapsing (text mode). */
    charLimit?: number;
    /** Modal heading. */
    title?: string;
    /** Rendered when there is nothing to show. */
    emptyText?: string;
    /** Overrides the default pill styling. */
    chipClassName?: string;
}

/**
 * Renders a long list (or a long string) compactly: the first few entries
 * inline, the rest behind a "+N more" trigger that opens a modal with the full
 * content. Keeps table row heights to a single line.
 */
const TruncatedList: React.FC<TruncatedListProps> = ({
    items,
    text,
    limit = 2,
    charLimit = 60,
    title = 'Details',
    emptyText = '-',
    chipClassName,
}) => {
    const styles = useStyles();
    const [isOpen, setOpen] = useState(false);

    const isTextMode = !items && typeof text === 'string';

    const list = useMemo(
        () =>
            (Array.isArray(items) ? items : []).filter(
                (item) => item !== null && item !== undefined && item !== '',
            ),
        [items],
    );

    const openModal = (event: React.MouseEvent) => {
        // Table rows are usually clickable — don't bubble into the row handler.
        event.stopPropagation();
        setOpen(true);
    };

    const modal = !!isOpen && (
        <Modal
            open={isOpen}
            title={title}
            subtitle={null}
            maxWidth="xs"
            onClose={() => setOpen(false)}
            actions={[
                <Button
                    key={1}
                    variant="contained"
                    color="default"
                    className="cancelButton"
                    onClick={() => setOpen(false)}
                    disableElevation
                >
                    Close
                </Button>,
            ]}
        >
            {isTextMode ? (
                <Typography className={styles.modalText}>{text}</Typography>
            ) : (
                <Box className={styles.modalList}>
                    {list.map((item, index) => (
                        <Typography
                            key={`${item}-${index}`}
                            className={chipClassName ?? styles.chip}
                        >
                            {item}
                        </Typography>
                    ))}
                </Box>
            )}
        </Modal>
    );

    if (isTextMode) {
        const value = text ?? '';
        if (!value) {
            return <Typography className={styles.empty}>{emptyText}</Typography>;
        }
        const isTruncated = value.length > charLimit;
        return (
            <Box className={styles.wrapper} onClick={(e) => e.stopPropagation()}>
                <Typography className={styles.modalText}>
                    {isTruncated ? `${value.slice(0, charLimit)}…` : value}
                </Typography>
                {isTruncated && (
                    <Button
                        className={styles.moreChip}
                        onClick={openModal}
                        disableElevation
                    >
                        more
                    </Button>
                )}
                {modal}
            </Box>
        );
    }

    if (!list.length) {
        return <Typography className={styles.empty}>{emptyText}</Typography>;
    }

    const visible = list.slice(0, limit);
    const hiddenCount = list.length - visible.length;

    return (
        <Box className={styles.wrapper} onClick={(e) => e.stopPropagation()}>
            {visible.map((item, index) => (
                <Typography
                    key={`${item}-${index}`}
                    className={chipClassName ?? styles.chip}
                    title={String(item)}
                >
                    {item}
                </Typography>
            ))}
            {hiddenCount > 0 && (
                <Button
                    className={styles.moreChip}
                    onClick={openModal}
                    disableElevation
                >
                    {`+${hiddenCount} more`}
                </Button>
            )}
            {modal}
        </Box>
    );
};

export default React.memo(TruncatedList);
